// src/admin/pages/cost/CostsManagement.tsx
import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Alert,
  CircularProgress,
  Stack,
  Chip,
  Tooltip,
  Avatar,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Collapse,
  Pagination,
  Backdrop,
  LinearProgress,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Payments as PaymentsIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Refresh as RefreshIcon,
  Repeat as RepeatIcon,
  EventNote as EventNoteIcon,
} from '@mui/icons-material';
import {
  Cost,
  CostCategory,
  CostType,
  CostStatus,
  COST_CATEGORY_LABELS,
  COST_TYPE_LABELS,
  RECURRENCE_FREQUENCY_LABELS,
} from './cost';
import { CostService } from './costService';
import CostDrawer from './CostDialog';
import {
  useCostsPaginated,
  useCostStats,
  useCostMutations,
} from '../../../hooks/useCosts';
import ToastNotification from '../common/Toastnotification';
import { useAuth } from '../../../contexts/AuthContext';
import { useCompany } from '../../../contexts/CompanyContext';
import { getCurrencySymbol } from '../../../util/productUtils';
import { formatCurrency } from '../../../util/Formatcurrency';

const COSTS_PER_PAGE = 10;

interface ToastState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'warning' | 'info';
}

const getSoftChipSx = (color: string, isDark: boolean = false) => {
  type PaletteEntry = { bg: string; border: string; text: string; iconColor: string };

  const lightPalette: Record<string, PaletteEntry> = {
    warning: { bg: 'rgba(255,167,38,0.12)', border: 'rgba(255,167,38,0.4)', text: '#b76e00', iconColor: '#e68900' },
    info: { bg: 'rgba(41,182,246,0.12)', border: 'rgba(41,182,246,0.4)', text: '#0277bd', iconColor: '#039be5' },
    primary: { bg: 'rgba(33,150,243,0.12)', border: 'rgba(33,150,243,0.4)', text: '#1565c0', iconColor: '#1976d2' },
    success: { bg: 'rgba(102,187,106,0.15)', border: 'rgba(102,187,106,0.45)', text: '#2e7d32', iconColor: '#43a047' },
    error: { bg: 'rgba(239,83,80,0.12)', border: 'rgba(239,83,80,0.4)', text: '#c62828', iconColor: '#e53935' },
    secondary: { bg: 'rgba(171,71,188,0.10)', border: 'rgba(171,71,188,0.35)', text: '#6a1b9a', iconColor: '#8e24aa' },
    default: { bg: 'rgba(158,158,158,0.12)', border: 'rgba(158,158,158,0.4)', text: '#424242', iconColor: '#757575' },
  };

  const darkPalette: Record<string, PaletteEntry> = {
    warning: { bg: 'rgba(255,167,38,0.22)', border: 'rgba(255,167,38,0.55)', text: '#ffcc80', iconColor: '#ffa726' },
    info: { bg: 'rgba(41,182,246,0.20)', border: 'rgba(41,182,246,0.50)', text: '#81d4fa', iconColor: '#29b6f6' },
    primary: { bg: 'rgba(33,150,243,0.22)', border: 'rgba(33,150,243,0.50)', text: '#90caf9', iconColor: '#42a5f5' },
    success: { bg: 'rgba(102,187,106,0.22)', border: 'rgba(102,187,106,0.50)', text: '#a5d6a7', iconColor: '#66bb6a' },
    error: { bg: 'rgba(239,83,80,0.22)', border: 'rgba(239,83,80,0.50)', text: '#ef9a9a', iconColor: '#ef5350' },
    secondary: { bg: 'rgba(171,71,188,0.22)', border: 'rgba(171,71,188,0.50)', text: '#ce93d8', iconColor: '#ab47bc' },
    default: { bg: 'rgba(158,158,158,0.22)', border: 'rgba(158,158,158,0.50)', text: '#e0e0e0', iconColor: '#bdbdbd' },
  };

  const palette = isDark ? darkPalette : lightPalette;
  const p = palette[color] ?? palette.default;

  return {
    backgroundColor: p.bg,
    borderColor: p.border,
    color: p.text,
    fontWeight: 600,
    border: `1px solid ${p.border}`,
    '& .MuiChip-icon': { color: p.iconColor },
    '& .MuiChip-label': { color: p.text },
  };
};

interface LoadingOverlayProps {
  wrapperRef: React.RefObject<HTMLDivElement | null>;
}

const LoadingOverlay: React.FC<LoadingOverlayProps> = ({ wrapperRef }) => {
  const [rect, setRect] = useState<DOMRect | null>(null);
  const theme = useTheme();

  const overlayBg = theme.palette.mode === 'dark'
    ? 'rgba(26, 29, 36, 0.85)'
    : 'rgba(255, 255, 255, 0.85)';

  React.useEffect(() => {
    const measure = () => {
      if (wrapperRef.current) setRect(wrapperRef.current.getBoundingClientRect());
    };
    measure();
    const el = wrapperRef.current;
    window.addEventListener('resize', measure);
    el?.addEventListener('scroll', measure);
    return () => {
      window.removeEventListener('resize', measure);
      el?.removeEventListener('scroll', measure);
    };
  }, [wrapperRef]);

  if (!rect) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        top: rect.top, left: rect.left,
        width: rect.width, height: rect.height,
        backgroundColor: overlayBg,
        backdropFilter: 'blur(2px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1100, pointerEvents: 'all', borderRadius: 'inherit',
      }}
    >
      <Box sx={{ textAlign: 'center' }}>
        <CircularProgress size={40} />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Loading costs...
        </Typography>
      </Box>
    </Box>
  );
};

const CostsManagement: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isDark = theme.palette.mode === 'dark';

  const { companyProfile } = useCompany();
  const currencySymbol = getCurrencySymbol(companyProfile?.currency || 'LKR');

  // ── UI state ──────────────────────────────────────────────────────────────
  const [deleteLoading, setDeleteLoading] = useState<string>('');
  const [toast, setToast] = useState<ToastState>({ open: false, message: '', severity: 'info' });

  // ── Pagination & filter state ─────────────────────────────────────────────
  const [currentPage, setCurrentPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [showFilters, setShowFilters] = useState(false);

  // ── Drawer state ──────────────────────────────────────────────────────────
  const [createDrawerOpen, setCreateDrawerOpen] = useState(false);
  const [editDrawerOpen, setEditDrawerOpen] = useState(false);
  const [selectedCost, setSelectedCost] = useState<Cost | null>(null);

  // ── Delete confirmation dialog state ──────────────────────────────────────
  const [deleteTarget, setDeleteTarget] = useState<Cost | null>(null);
  const [deleteDialogLoading, setDeleteDialogLoading] = useState(false);
  const [deletingOverlay, setDeletingOverlay] = useState(false);

  const tableWrapperRef = useRef<HTMLDivElement>(null);

  const { hasPermission, isSuperAdmin } = useAuth();

  const canView   = isSuperAdmin || hasPermission('costs.view');
  const canCreate = isSuperAdmin || hasPermission('costs.create');
  const canEdit   = isSuperAdmin || hasPermission('costs.edit');
  const canDelete = isSuperAdmin || hasPermission('costs.delete');

  // ── React Query hooks ─────────────────────────────────────────────────────
  const {
    data: costsData,
    isLoading: costsLoading,
    isFetching: costsIsFetching,
    isError: costsIsError,
    error: costsErrorObj,
  } = useCostsPaginated({
    page: currentPage,
    search: searchQuery,
    category: categoryFilter,
    type: typeFilter,
    status: statusFilter,
    pageSize: COSTS_PER_PAGE,
  });

  const { data: statsData } = useCostStats();
  const { remove } = useCostMutations();

  // ── Derived values ────────────────────────────────────────────────────────
  const costs = costsData?.costs ?? [];
  const totalCosts = costsData?.totalCount ?? 0;
  const totalPages = Math.ceil(totalCosts / COSTS_PER_PAGE);
  const loading = costsLoading || costsIsFetching;

  const totalCount = statsData?.totalCount ?? 0;
  const recurringCount = statsData?.recurringCount ?? 0;
  const oneTimeCount = statsData?.oneTimeCount ?? 0;
  const activeCount = statsData?.activeCount ?? 0;
  const totalAmountAllTime = statsData?.totalAmountAllTime ?? 0;
  const totalAmountThisMonth = statsData?.totalAmountThisMonth ?? 0;

  // ── Helpers ───────────────────────────────────────────────────────────────
  const showToast = (message: string, severity: 'success' | 'error' | 'warning' | 'info') => {
    setToast({ open: true, message, severity });
  };
  const handleCloseToast = () => setToast(prev => ({ ...prev, open: false }));

  const hasActiveFilters = searchQuery !== '' || categoryFilter !== 'all' || typeFilter !== 'all' || statusFilter !== 'all';
  const activeFiltersCount = [
    searchQuery !== '', categoryFilter !== 'all', typeFilter !== 'all', statusFilter !== 'all',
  ].filter(Boolean).length;

  const resetToFirstPage = () => setCurrentPage(1);

  const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
  };

  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      setSearchQuery(searchInput);
      resetToFirstPage();
    }
  };

  const clearFilters = () => {
    setSearchInput('');
    setSearchQuery('');
    setCategoryFilter('all');
    setTypeFilter('all');
    setStatusFilter('all');
    resetToFirstPage();
    showToast('Filters cleared', 'info');
  };

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDeleteCost = (cost: Cost) => setDeleteTarget(cost);

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      setDeleteDialogLoading(true);
      setDeleteLoading(deleteTarget.id!);
      setDeleteTarget(null);
      setDeletingOverlay(true);

      await remove.mutateAsync(deleteTarget.id!);

      resetToFirstPage();
      showToast('Cost deleted successfully', 'success');
    } catch (err: any) {
      showToast(`Failed to delete cost: ${err.message || 'Unknown error'}`, 'error');
    } finally {
      setDeleteDialogLoading(false);
      setDeleteLoading('');
      setDeletingOverlay(false);
    }
  };

  if (!canView) {
    return (
      <Box sx={{ p: 3 }}>
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <PaymentsIcon sx={{ fontSize: 64, color: 'warning.main', mb: 2, opacity: 0.7 }} />
            <Typography variant="h5" gutterBottom>Access Denied</Typography>
            <Typography variant="body1" color="text.secondary">
              You don't have permission to view costs. Please contact your administrator.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  const handleDeleteCancel = () => setDeleteTarget(null);

  const handleEditCost = (cost: Cost) => {
    if (!canEdit) return;
    setSelectedCost(cost);
    setEditDrawerOpen(true);
  };

  const handleDrawerSuccess = () => resetToFirstPage();

  const handleRefresh = () => {
    resetToFirstPage();
    showToast('Costs refreshed', 'info');
  };

  if (loading && costs.length === 0) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight="400px">
        <CircularProgress size={40} />
        <Typography variant="body1" sx={{ ml: 2 }}>Loading costs...</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: { md: '100%' },
        display: 'flex',
        flexDirection: 'column',
        overflow: { xs: 'visible', md: 'hidden' },
        p: { xs: 2, sm: 3 },
      }}
    >
      {/* ── Fixed Header ── */}
      <Box sx={{ flexShrink: 0 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={2}
          sx={{ mb: 3 }}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <PaymentsIcon sx={{ fontSize: { xs: 28, md: 32 }, color: 'primary.main' }} />
            <Box>
              <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.5rem', md: '2rem' } }}>
                Costs
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Track overhead, operating, and other business costs
              </Typography>
            </Box>
          </Stack>
          {canCreate && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateDrawerOpen(true)}
              sx={{ alignSelf: { xs: 'flex-start', sm: 'auto' } }}
            >
              Add Cost
            </Button>
          )}
        </Stack>

        {costsIsError && (
          <Alert
            severity="error"
            sx={{ mb: 3 }}
            action={
              <Button color="inherit" size="small" onClick={handleRefresh} startIcon={<RefreshIcon />}>
                Retry
              </Button>
            }
          >
            {(costsErrorObj as Error)?.message || 'Failed to fetch costs'}
          </Alert>
        )}

        {/* ── Stats Cards ── */}
        <Grid container spacing={{ xs: 1, sm: 1.5, md: 2 }} sx={{ mb: 2 }} alignItems="stretch">
          <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
            <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ p: { xs: 1, sm: 1.25 }, '&:last-child': { pb: { xs: 1, sm: 1.25 } }, flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Typography color="text.secondary" gutterBottom variant="caption" noWrap>Total Costs</Typography>
                <Typography variant="h5" component="div" color="text.primary"
                  sx={{ fontSize: { xs: '1rem', sm: '1.125rem', md: '1.375rem' }, mb: 0.5 }}>
                  {totalCount}
                </Typography>
                <Box sx={{ mt: 'auto', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'info.main', flexShrink: 0 }} />
                    <Typography variant="caption" color="info.main" noWrap>{recurringCount} Recurring</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'text.disabled', flexShrink: 0 }} />
                    <Typography variant="caption" color="text.secondary" noWrap>{oneTimeCount} One-Time</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
            <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ p: { xs: 1, sm: 1.25 }, '&:last-child': { pb: { xs: 1, sm: 1.25 } }, flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Typography color="text.secondary" gutterBottom variant="caption" noWrap>Active</Typography>
                <Typography variant="h5" component="div" color="text.primary"
                  sx={{ fontSize: { xs: '1rem', sm: '1.125rem', md: '1.375rem' }, mb: 0.5 }}>
                  {activeCount}
                </Typography>
                <Box sx={{ mt: 'auto' }}>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {totalCount > 0 ? `${((activeCount / totalCount) * 100).toFixed(1)}% of total` : '—'}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
            <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ p: { xs: 1, sm: 1.25 }, '&:last-child': { pb: { xs: 1, sm: 1.25 } }, flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Typography color="text.secondary" gutterBottom variant="caption" noWrap>This Month</Typography>
                <Typography variant="h5" component="div" color="text.primary"
                  sx={{ fontSize: { xs: '1.1rem', sm: '1.35rem', md: '1.75rem' }, mb: 1 }} noWrap>
                  {formatCurrency(totalAmountThisMonth, currencySymbol)}
                </Typography>
                <Box sx={{ mt: 'auto' }}>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    Active recurring + this month's one-time
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
            <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ p: { xs: 1, sm: 1.25 }, '&:last-child': { pb: { xs: 1, sm: 1.25 } }, flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Typography color="text.secondary" gutterBottom variant="caption" noWrap>All-Time Total</Typography>
                <Typography variant="h5" component="div" color="text.primary"
                  sx={{ fontSize: { xs: '1.1rem', sm: '1.35rem', md: '1.75rem' }, mb: 1 }} noWrap>
                  {formatCurrency(totalAmountAllTime, currencySymbol)}
                </Typography>
                <Box sx={{ mt: 'auto' }}>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    Sum of every recorded cost
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* ── Search and Filters ── */}
        <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 2 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            justifyContent="space-between"
          >
            <TextField
              placeholder="Search costs... (Press Enter)"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyPress={handleSearchKeyPress}
              sx={{ flexGrow: 1, maxWidth: { sm: 400 } }}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start"><SearchIcon /></InputAdornment>
                ),
              }}
            />
            <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
              <Button
                onClick={() => setShowFilters(!showFilters)}
                variant={hasActiveFilters ? 'contained' : 'outlined'}
                startIcon={<FilterListIcon />}
                endIcon={showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                size="small"
              >
                Filters {hasActiveFilters && `(${activeFiltersCount})`}
              </Button>
              {hasActiveFilters && (
                <Button onClick={clearFilters} variant="text" size="small" startIcon={<ClearIcon />}>
                  Clear all
                </Button>
              )}
            </Stack>
          </Stack>

          <Collapse in={showFilters}>
            <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Category</InputLabel>
                    <Select
                      value={categoryFilter}
                      onChange={(e) => { setCategoryFilter(e.target.value); resetToFirstPage(); }}
                      label="Category"
                    >
                      <MenuItem value="all">All Categories</MenuItem>
                      {Object.values(CostCategory).map((cat) => (
                        <MenuItem key={cat} value={cat}>{COST_CATEGORY_LABELS[cat]}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Type</InputLabel>
                    <Select
                      value={typeFilter}
                      onChange={(e) => { setTypeFilter(e.target.value); resetToFirstPage(); }}
                      label="Type"
                    >
                      <MenuItem value="all">All Types</MenuItem>
                      {Object.values(CostType).map((type) => (
                        <MenuItem key={type} value={type}>{COST_TYPE_LABELS[type]}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={statusFilter}
                      onChange={(e) => { setStatusFilter(e.target.value); resetToFirstPage(); }}
                      label="Status"
                    >
                      <MenuItem value="all">All Status</MenuItem>
                      <MenuItem value={CostStatus.ACTIVE}>Active</MenuItem>
                      <MenuItem value={CostStatus.PAUSED}>Paused</MenuItem>
                      <MenuItem value={CostStatus.ENDED}>Ended</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Box sx={{ display: 'flex', alignItems: 'center', height: '40px' }}>
                    <Typography variant="body2" color="text.secondary">
                      {hasActiveFilters
                        ? `Found ${costs.length} costs matching your filters`
                        : `Showing ${costs.length} costs on this page`}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Collapse>
        </Paper>
      </Box>

      {/* ── Scrollable Body ── */}
      <Box
        sx={{
          flex: 1,
          overflow: { xs: 'visible', md: 'auto' },
          display: 'flex',
          flexDirection: 'column',
          minHeight: { xs: 'auto', md: 0 },
        }}
      >
        {costs.length === 0 && !loading && (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
            <Card sx={{ width: '100%', maxWidth: 500 }}>
              <CardContent sx={{ textAlign: 'center', py: 6 }}>
                <PaymentsIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
                <Typography variant="h5" color="text.secondary" gutterBottom>
                  {hasActiveFilters ? 'No Costs Match Your Filters' : 'No Costs Recorded Yet'}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}>
                  {hasActiveFilters
                    ? 'Try adjusting your search or filter criteria.'
                    : 'Add your first overhead or operating cost to start tracking business expenses.'}
                </Typography>
                {hasActiveFilters ? (
                  <Button onClick={clearFilters} variant="outlined" startIcon={<ClearIcon />}>
                    Clear Filters
                  </Button>
                ) : canCreate && (
                  <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateDrawerOpen(true)}>
                    Add Cost
                  </Button>
                )}
              </CardContent>
            </Card>
          </Box>
        )}

        {costs.length > 0 && (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: { xs: 'auto', md: 0 } }}>
            <TableContainer
              component={Paper}
              ref={tableWrapperRef}
              sx={{
                flex: 1,
                overflow: { xs: 'auto', md: 'auto' },
                maxHeight: { md: '100%' },
                mb: 2,
                position: 'relative',
                '& .MuiTable-root': { minWidth: { xs: 700, md: 900 } },
              }}
            >
              <Table stickyHeader size={isMobile ? 'small' : 'medium'}>
                <TableHead>
                  <TableRow>
                    <TableCell>Cost</TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Category</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Date</TableCell>
                    <TableCell>Status</TableCell>
                    {(canEdit || canDelete) && (
                      <TableCell align="right">Actions</TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {costs.map((cost) => (
                    <TableRow
                      key={cost.id}
                      hover
                      sx={{
                        '&:hover': { backgroundColor: 'action.hover' },
                        '&:last-child td, &:last-child th': { borderBottom: 0 },
                      }}
                    >
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Avatar sx={{ bgcolor: 'primary.main', width: { xs: 32, sm: 40 }, height: { xs: 32, sm: 40 } }}>
                            <PaymentsIcon fontSize="small" />
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: { xs: '0.85rem', sm: '1rem' } }}>
                              {cost.title}
                            </Typography>
                            {cost.vendor && (
                              <Typography variant="caption" color="text.secondary">
                                {cost.vendor}
                              </Typography>
                            )}
                            <Typography variant="caption" color="text.secondary"
                              sx={{ display: { xs: 'block', sm: 'none' } }}>
                              {COST_CATEGORY_LABELS[cost.category]}
                            </Typography>
                          </Box>
                        </Stack>
                      </TableCell>

                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        <Typography variant="body2">{COST_CATEGORY_LABELS[cost.category]}</Typography>
                      </TableCell>

                      <TableCell>
                        <Chip
                          icon={cost.costType === CostType.RECURRING ? <RepeatIcon /> : <EventNoteIcon />}
                          label={
                            cost.costType === CostType.RECURRING && cost.recurrenceFrequency
                              ? RECURRENCE_FREQUENCY_LABELS[cost.recurrenceFrequency]
                              : COST_TYPE_LABELS[cost.costType]
                          }
                          size="small"
                          variant="outlined"
                          sx={getSoftChipSx(cost.costType === CostType.RECURRING ? 'info' : 'default', isDark)}
                        />
                      </TableCell>

                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {formatCurrency(cost.amount, currencySymbol)}
                        </Typography>
                        {cost.costType === CostType.RECURRING && cost.recurrenceFrequency && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            ~{formatCurrency(CostService.annualizedAmount(cost.amount, cost.recurrenceFrequency), currencySymbol)}/yr
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        <Typography variant="body2">
                          {cost.incurredDate.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
                        </Typography>
                        {cost.endDate && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            ends {cost.endDate.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell>
                        <Chip
                          label={cost.status.charAt(0).toUpperCase() + cost.status.slice(1)}
                          size="small"
                          variant="outlined"
                          sx={getSoftChipSx(
                            cost.status === CostStatus.ACTIVE ? 'success'
                              : cost.status === CostStatus.PAUSED ? 'warning' : 'default',
                            isDark
                          )}
                        />
                      </TableCell>

                      {(canEdit || canDelete) && (
                        <TableCell align="right">
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end" flexWrap="nowrap">
                            {canEdit && (
                              <Tooltip title="Edit">
                                <IconButton onClick={() => handleEditCost(cost)} size="small" color="warning">
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {canDelete && (
                              <Tooltip title="Delete">
                                <IconButton
                                  onClick={() => handleDeleteCost(cost)}
                                  disabled={deleteLoading === cost.id}
                                  size="small" color="error"
                                >
                                  {deleteLoading === cost.id
                                    ? <CircularProgress size={16} />
                                    : <DeleteIcon fontSize="small" />}
                                </IconButton>
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {loading && <LoadingOverlay wrapperRef={tableWrapperRef} />}

            <Paper elevation={2} sx={{ flexShrink: 0, borderRadius: 1 }}>
              <Box sx={{ py: 2, px: 2 }}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={{ xs: 1.5, sm: 3 }}
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ width: '100%' }}
                >
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    Showing {totalCosts === 0 ? 0 : (currentPage - 1) * COSTS_PER_PAGE + 1}–
                    {Math.min(currentPage * COSTS_PER_PAGE, totalCosts)} of {totalCosts} costs
                  </Typography>
                  <Pagination
                    count={Math.max(1, totalPages)}
                    page={currentPage}
                    onChange={handlePageChange}
                    color="primary"
                    size={isMobile ? 'small' : 'medium'}
                    showFirstButton
                    showLastButton
                    disabled={loading || totalPages <= 1}
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    Page {currentPage} of {Math.max(1, totalPages)}
                  </Typography>
                </Stack>
              </Box>
            </Paper>
          </Box>
        )}
      </Box>

      {/* ── Create Cost Drawer ── */}
      <CostDrawer
        open={createDrawerOpen}
        onClose={() => setCreateDrawerOpen(false)}
        onSuccess={handleDrawerSuccess}
        onShowToast={showToast}
      />

      {/* ── Edit Cost Drawer (only when a cost is selected) ── */}
      {selectedCost && (
        <CostDrawer
          open={editDrawerOpen}
          onClose={() => { setEditDrawerOpen(false); setSelectedCost(null); }}
          onSuccess={handleDrawerSuccess}
          cost={selectedCost}
          onShowToast={showToast}
        />
      )}

      {/* ── Delete Confirmation Dialog ── */}
      <Dialog open={!!deleteTarget} onClose={handleDeleteCancel} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Cost</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete <strong>"{deleteTarget?.title}"</strong>?{' '}
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleDeleteCancel} disabled={deleteDialogLoading}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
            disabled={deleteDialogLoading}
            startIcon={deleteDialogLoading ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {deleteDialogLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Full-screen deleting overlay ── */}
      <Backdrop
        open={deletingOverlay}
        sx={{ zIndex: (t) => t.zIndex.modal + 100, flexDirection: 'column', gap: 3, bgcolor: 'rgba(0,0,0,0.6)' }}
      >
        <Box
          sx={{
            bgcolor: 'background.paper', borderRadius: 3, px: 5, py: 4,
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2.5,
            minWidth: 280, boxShadow: 24,
          }}
        >
          <CircularProgress size={48} thickness={4} />
          <Box sx={{ width: '100%' }}>
            <LinearProgress sx={{ borderRadius: 1, mb: 1.5 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600, textAlign: 'center', color: 'text.primary' }}>
              Deleting Cost…
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 0.5 }}>
              Please wait, do not close this window
            </Typography>
          </Box>
        </Box>
      </Backdrop>

      <ToastNotification
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        onClose={handleCloseToast}
      />
    </Box>
  );
};

export default CostsManagement;
